import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibContainer } from '@chewy/kib-layout-react'

import { SwapProductDialogSteps } from '~/components/common/dialog/swapProduct/types'
import ReplaceIcon from '~/components/elements/Icons/generated/ReplaceIcon'
import DialogNames from '~/constants/DialogNames'
import { getSwapProductTrackingData } from '~/constants/swapProductAnalytics'
import { getPatientsForUser } from '~/store/reducers/users'
import type { Prescription } from '~/types/entities/prescriptions'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'
import useDialog from '~/utils/useDialog'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-layout-styles/dist/styles/kib-container.css'
import styles from './SwapProductButtons.module.scss'

interface SwapProductButtonsProps {
  disabled?: boolean
  prescription: Prescription
}

const SwapProductButtons = ({
  disabled = false,
  prescription,
}: SwapProductButtonsProps) => {
  const { t } = useTranslation(['Common', 'Dialogs'])

  const [openSwapProductDialog] = useDialog(DialogNames.SWAP_PRODUCT)

  const clientPatients = useSelector(
    getPatientsForUser(prescription.clientId || ''),
  )
  const hasMultiplePatients = clientPatients.length > 1

  return (
    <KibContainer className={styles.container}>
      <KibButtonNew
        disabled={disabled}
        emphasis="secondary"
        icon={<ReplaceIcon />}
        size="small"
        onClick={() => {
          trackEvent(
            TrackingEvent.SWAP_PRODUCT_BUTTON,
            getSwapProductTrackingData('SWAP_PRODUCT_BUTTON', prescription),
          )
          openSwapProductDialog({
            initialStep: SwapProductDialogSteps.CHANGE_PRODUCT,
            initialPrescription: prescription,
          })
        }}
      >
        {t('Common:CHANGE_PRODUCT')}
      </KibButtonNew>
      <KibButtonNew
        disabled={disabled}
        emphasis="secondary"
        icon={<ReplaceIcon />}
        size="small"
        onClick={() => {
          trackEvent(
            TrackingEvent.SWAP_COUNT_BUTTON,
            getSwapProductTrackingData('SWAP_COUNT_BUTTON', prescription),
          )
          openSwapProductDialog({
            initialStep: SwapProductDialogSteps.EDIT_RX,
            initialPrescription: prescription,
          })
        }}
      >
        {t('Common:CHANGE_COUNT')}
      </KibButtonNew>
      {hasMultiplePatients && (
        <KibButtonNew
          disabled={disabled}
          emphasis="secondary"
          icon={<ReplaceIcon />}
          size="small"
          onClick={() => {
            trackEvent(
              TrackingEvent.SWAP_PATIENT_BUTTON,
              getSwapProductTrackingData('SWAP_PATIENT_BUTTON', prescription),
            )
            openSwapProductDialog({
              initialStep: SwapProductDialogSteps.CHANGE_PATIENT,
              initialPrescription: prescription,
            })
          }}
        >
          {t('Common:CHANGE_PATIENT')}
        </KibButtonNew>
      )}
    </KibContainer>
  )
}

export default SwapProductButtons
